import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortDishInfo } from 'types';
import { IIDsShowInComboDishes } from '../../../../../../../../../../createDishState/createDishContext';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortDishInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setDishModifications: (value: string[]) => void;
	selectedDishModifications: string[];
	selectedDishModificationSubCategory: Nullable<string>;
	setIDsShowInComboDishes: (value: IIDsShowInComboDishes) => void;
	IDsShowInComboDishes: IIDsShowInComboDishes;
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedDishModificationSubCategory,
	setDishModifications,
	selectedDishModifications,
	setIDsShowInComboDishes,
	IDsShowInComboDishes,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const [showInCombo, setSowInCombo] = useState<boolean>(false);

	const filteredDataWithAdditionalContent = useMemo(() => {
		const updatedData = data.map((el) => ({
			...el,
			additionalContent: (
				<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
			),
		}));

		if (searchString) {
			const filteredDataByName = updatedData.filter((el) =>
				el.name.toLowerCase().includes(searchString.toLowerCase()),
			);
			const filteredDataByExternalId = updatedData.filter((el) =>
				el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
			);
			const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
			return unionFilteredData;
		}
		return updatedData;
	}, [searchString, data]);

	const handleSelectAllData = useCallback(() => {
		const isCheckedAllItems = data.every((el) => selectedDishModifications.includes(el.id));
		const dataIDs = data.map((el) => el.id);
		const copyIDsShowInComboDishes = { ...IDsShowInComboDishes };
		if (isCheckedAllItems) {
			setDishModifications(selectedDishModifications.filter((el) => !dataIDs.includes(el)));

			dataIDs.forEach((dishId) => delete copyIDsShowInComboDishes[dishId]);
			setIDsShowInComboDishes(copyIDsShowInComboDishes);
		} else {
			const filteredData = data.map((el) => el.id).filter((el) => !selectedDishModifications.includes(el));
			setDishModifications([...selectedDishModifications, ...filteredData]);

			filteredData.forEach((dishId) => (copyIDsShowInComboDishes[dishId] = showInCombo));
			setIDsShowInComboDishes(copyIDsShowInComboDishes);
		}
	}, [
		data,
		selectedDishModifications,
		showInCombo,
		IDsShowInComboDishes,
		setDishModifications,
		setIDsShowInComboDishes,
	]);

	const changeShowInCombo = useCallback(
		(e: CheckboxChangeEvent) => {
			setSowInCombo(e.target.checked);
		},
		[setSowInCombo],
	);

	const addIdForIDsShowInComboDishes = useCallback(
		(dishId: string) => {
			const copyIDsShowInComboDishes = { ...IDsShowInComboDishes };
			copyIDsShowInComboDishes[dishId] = showInCombo;
			setIDsShowInComboDishes(copyIDsShowInComboDishes);
		},
		[setIDsShowInComboDishes, IDsShowInComboDishes, showInCombo],
	);

	const removeIdFromIDsShowInComboDishes = useCallback(
		(dishId: string) => {
			const copyIDsShowInComboDishes = { ...IDsShowInComboDishes };
			delete copyIDsShowInComboDishes[dishId];
			setIDsShowInComboDishes(copyIDsShowInComboDishes);
		},
		[setIDsShowInComboDishes, IDsShowInComboDishes],
	);

	useEffect(() => {
		setSearchString('');
	}, [selectedDishModificationSubCategory]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const isChecked = () => {
				return data.every((el) => selectedDishModifications.includes(el.id));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
						<Checkbox
							className={style.checkBox}
							checked={!!(data.length && isChecked())}
							disabled={!data.length}
							onChange={handleSelectAllData}>
							<Text translationNameSpace="form" translationKey="selectAllDishesLabel" asFragment={true} />
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showInCombo} onChange={changeShowInCombo}>
							<Text translationNameSpace="form" translationKey="showInComboLabel" asFragment={true} />
						</Checkbox>
					</div>
					{menu}
				</>
			);
		},
		[
			handleSelectAllData,
			setSearchString,
			changeShowInCombo,
			searchString,
			showInCombo,
			data,
			selectedDishModifications,
		],
	);

	return (
		<FormSelect
			name="selectedDishModifications"
			label="dishesLabel"
			options={filteredDataWithAdditionalContent}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={!selectedDishModificationSubCategory || isLoading}
			onChange={setDishModifications}
			onSelect={addIdForIDsShowInComboDishes}
			onDeselect={removeIdFromIDsShowInComboDishes}
			counterValue={selectedDishModifications.length}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
